import { XIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFetchReviews } from '../data'
import { useReviewsConfig } from '../providers'
import { ReviewCard } from './partials/review-card'
import ReviewStars from './partials/review-stars'

export function ReviewsBadge() {
  const options = useReviewsConfig()
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useFetchReviews(options.filter)

  const reviews = data?.pages?.flatMap((page) => page.result || []) || []
  const totalReviews = data?.pages?.[0]?.pagination?.total_items || 0
  const averageRating = data?.pages?.[0]?.pagination?.average_rating

  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isOpening, setIsOpening] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus()
    }
  }, [isOpen])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setIsOpening(true)
    setTimeout(() => {
      setIsOpening(false)
    }, 300)
  }, [])

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, 300)
  }, [])

  if (!averageRating || !totalReviews) {
    return null
  }

  const badgeClasses = [
    'homerunner-reviews-badge inline-flex flex-col items-center gap-2 bg-card text-card-foreground rounded-lg shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow duration-200 border border-border',
  ]

  if (options.badge?.type === 'floating') {
    badgeClasses.push('fixed z-50')
    switch (options.badge?.position) {
      case 'top-left':
        badgeClasses.push('top-4 left-4')
        break
      case 'top-right':
        badgeClasses.push('top-4 right-4')
        break
      case 'bottom-left':
        badgeClasses.push('bottom-4 left-4')
        break
      case 'bottom-right':
        badgeClasses.push('bottom-4 right-4')
        break
    }
  }

  return (
    <>
      <div className={badgeClasses.join(' ')} onClick={handleOpen}>
        <div className="homerunner-reviews-badge-label text-card-foreground text-lg font-semibold">
          Reviews
        </div>

        {!options.hideOverallRating && (
          <div className="homerunner-reviews-badge-rating flex items-center gap-2">
            <span className="homerunner-reviews-badge-average-rating text-card-foreground text-2xl font-bold">
              {averageRating.toFixed(1)}
            </span>
            <div className="homerunner-reviews-badge-stars">
              <ReviewStars rating={averageRating} starClass="w-5 h-5" />
            </div>
          </div>
        )}

        <div className="homerunner-reviews-badge-count text-muted-foreground text-sm underline">
          {options.hideOverallRating
            ? `Read all reviews`
            : `Read our ${totalReviews.toLocaleString()} reviews`}
        </div>
      </div>

      {(isOpen || isClosing) && (
        <>
          <div
            className="homerunner-reviews-badge-backdrop bg-background/80 fixed inset-0 z-50 backdrop-blur-sm transition-opacity duration-300 ease-out"
            style={{ opacity: isClosing ? 0 : 1 }}
            onClick={handleClose}
          />

          <div
            ref={dialogRef}
            className="homerunner-reviews-badge-modal bg-background border-border fixed top-0 left-0 z-50 flex h-full w-full max-w-md transform flex-col border-r shadow-lg transition-transform duration-300 ease-out"
            style={{
              height: '100vh',
              transform: isOpening
                ? 'translateX(-100%)'
                : isClosing
                  ? 'translateX(-100%)'
                  : 'translateX(0)',
            }}
          >
            <div className="homerunner-reviews-badge-modal-header border-border bg-card flex-shrink-0 border-b p-6">
              <div className="homerunner-reviews-badge-modal-header-content flex items-center justify-between">
                <h2 className="homerunner-reviews-badge-modal-title text-card-foreground text-xl font-semibold">
                  {options.badge?.modalTitle ?? 'Reviews'}
                </h2>
                <button
                  onClick={handleClose}
                  className="homerunner-reviews-badge-modal-close focus:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                >
                  <XIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              {!options.hideOverallRating && (
                <div className="homerunner-reviews-badge-modal-rating mt-3 flex items-center gap-2">
                  <span className="homerunner-reviews-badge-modal-average-rating text-card-foreground text-2xl font-bold">
                    {averageRating.toFixed(1)}
                  </span>
                  <div className="homerunner-reviews-badge-modal-stars">
                    <ReviewStars rating={averageRating} starClass="w-5 h-5" />
                  </div>
                  <span className="homerunner-reviews-badge-modal-review-count text-muted-foreground text-sm">
                    ({totalReviews.toLocaleString()})
                  </span>
                </div>
              )}
            </div>

            <div
              className="homerunner-reviews-badge-modal-content min-h-0 flex-1 overflow-y-auto"
              style={{ padding: '24px' }}
            >
              <div className="homerunner-reviews-badge-modal-reviews-list space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="homerunner-reviews-badge-modal-review-item">
                    <ReviewCard review={review} />
                  </div>
                ))}

                {hasNextPage && (
                  <div className="homerunner-reviews-badge-modal-load-more pt-4">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="homerunner-reviews-badge-modal-load-more-button bg-accent text-primary-foreground hover:bg-accent/90 w-full px-6 py-3 rounded-md font-medium transition-colors shadow"
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load More Reviews'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
